import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import Viewer from "@samvera/clover-iiif/viewer";

const DEFAULT_OPTIONS = {
  canvasHeight: "100%",
  showTitle: false,
  showIIIFBadge: false,
  showDownload: false,
  informationPanel: {
    open: false,
    renderAbout: false,
    renderAnnotation: false,
    renderSupplementing: false,
    renderToggle: false,
    renderContentSearch: false,
  },
};

function normalizeOptions(options = {}) {
  const legacyHideInformationPanel = options.showInformationPanel === false;

  const informationPanel = {
    ...DEFAULT_OPTIONS.informationPanel,
    ...(options.informationPanel || {}),
  };

  if (legacyHideInformationPanel) {
    informationPanel.open = false;
    informationPanel.renderAbout = false;
    informationPanel.renderAnnotation = false;
    informationPanel.renderSupplementing = false;
    informationPanel.renderToggle = false;
    informationPanel.renderContentSearch = false;
  }

  return {
    ...DEFAULT_OPTIONS,
    ...options,
    informationPanel,
  };
}

function CloverOverlayViewer({ manifestUrl, options = {} }) {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);

  const viewerOptions = useMemo(() => {
    return normalizeOptions(options);
  }, [options]);

  useEffect(() => {
    if (!manifestUrl) {
      setError(new Error("Missing Clover manifest URL"));
      return;
    }

    const controller = new AbortController();

    setManifest(null);
    setError(null);

    fetch(manifestUrl, {
      method: "GET",
      credentials: "same-origin",
      signal: controller.signal,
      headers: {
        Accept: "application/ld+json, application/json;q=0.9, */*;q=0.1",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Could not load IIIF manifest: ${response.status} ${response.statusText}`
          );
        }

        return response.json();
      })
      .then((json) => {
        setManifest(json);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Clover overlay manifest load failed:", err);
          setError(err);
        }
      });

    return () => {
      controller.abort();
    };
  }, [manifestUrl]);

  if (error) {
    return (
      <div
        style={{
          color: "#fff",
          padding: "1rem",
        }}
      >
        Could not load IIIF manifest.
      </div>
    );
  }

  if (!manifest) {
    return (
      <div
        style={{
          color: "#fff",
          padding: "1rem",
        }}
      >
        Loading IIIF manifest…
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "400px",
        zIndex: 0,
      }}
    >
      <Viewer
        key={manifest.id || manifest["@id"] || manifestUrl}
        iiifContent={manifest}
        options={viewerOptions}
      />
    </div>
  );
}

window.KSWCloverReact = {
  mount(element, manifestUrl, options = {}) {
    if (!element || !manifestUrl) {
      return;
    }

    if (element.__kswCloverRoot) {
      element.__kswCloverRoot.render(
        <CloverOverlayViewer
          manifestUrl={manifestUrl}
          options={options}
        />
      );
      return;
    }

    const root = createRoot(element);

    root.render(
      <CloverOverlayViewer
        manifestUrl={manifestUrl}
        options={options}
      />
    );

    element.__kswCloverRoot = root;
    element.dataset.reactMounted = "true";
  },

  unmount(element) {
    if (!element || !element.__kswCloverRoot) {
      return;
    }

    element.__kswCloverRoot.unmount();
    delete element.__kswCloverRoot;
    delete element.dataset.reactMounted;
  },
};